import { consume } from '@lit/context';
import { LitElement, css, html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';

import { gameApiContext } from '../../context';
import type { GameAPIType } from '../../GameAPI';
import { scrollbarThin } from '../../theme/scrollbar.css';
import { tokens } from '../../theme/tokens.css';
import { uiState as uiStateProxy } from '../../UIState';
import { bindValtio } from '../../ValtioController';

/**
 * Big Message - Full-screen blocking overlay
 *
 * Displays critical messages that block all interaction:
 * - Mobile mode welcome (dismissible via tap)
 * - WebGL required error (non-dismissible)
 *
 * Renders based on uiState.bigMessage. Returns nothing when null.
 *
 * @element big-message
 */
@customElement('big-message')
export class BigMessage extends LitElement {
  static override styles = [
    tokens,
    scrollbarThin,
    css`
      :host {
        display: block;
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
        /* Declared here rather than inherited from the body — the component
           owns its own typography. */
        font-family: var(--font-family);
        /* The overlay is tap-to-dismiss, so a slow tap must not start an iOS
           text selection / magnifier on the copy under the finger. Re-asserted
           locally rather than leaning on the body's globals. */
        -webkit-user-select: none;
        user-select: none;
        -webkit-touch-callout: none;
        /* Top of the app z ladder — blocks interaction over panels (300) and
           the context menu (400); see .panel-overlay in airmash-ui. */
        z-index: 500;
      }

      .overlay {
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        background-color: rgba(0, 0, 0, 0.8);
        text-align: center;
        font-weight: bold;
        box-sizing: border-box;
        /* Fluid padding so the message breathes on phones and desktop alike. */
        padding: clamp(16px, 5vw, 40px);
        pointer-events: auto;
        /* The mobile welcome is dismissed by tapping the overlay — kill the
           full-screen grey tap-flash and the double-tap-zoom delay. (Inherited,
           so it covers the child text too. The webgl overlay has no click
           handler, so it's unaffected.) */
        touch-action: manipulation;
        -webkit-tap-highlight-color: transparent;
        /* Scroll (rather than clip) if the message is taller than the viewport,
           e.g. a short landscape phone; the thin bar is the shared scrollbarThin. */
        overflow-y: auto;
      }

      /* Auto margins on the first/last child center the block vertically while
         collapsing under overflow — so a too-tall message scrolls from the top
         instead of clipping (the usual flex justify-content: center pitfall). */
      .overlay > :first-child {
        margin-top: auto;
      }

      .overlay > :last-child {
        margin-bottom: auto;
      }

      /* Headline and its supporting paragraph share a reading measure. */
      .msg,
      .small {
        max-width: 560px;
      }

      .msg {
        font-size: clamp(22px, 6vw, 28px);
        color: var(--color-text-white);
      }

      .small {
        font-size: clamp(15px, 4vw, 20px);
        color: var(--color-text-link);
        margin: clamp(16px, 4vh, 30px) 0;
        line-height: 160%;
      }

      /* Footnotes below the paragraph: .tip is a labelled callout, .aside a
         plain secondary line. Same measure and type, different emphasis. */
      .tip,
      .aside {
        max-width: min(420px, 100%);
        margin: 0 0 24px;
        font-size: clamp(13px, 3.5vw, 15px);
        font-weight: normal;
        line-height: 150%;
        color: var(--color-text-muted);
      }

      /* Lighter tint over the dark overlay; one-off bespoke value (no theme
         token yet). border-box keeps the padding inside the 100% cap. */
      .tip {
        box-sizing: border-box;
        padding: 10px 14px;
        border-radius: var(--radius-md);
        background-color: rgba(255, 255, 255, 0.06);
      }

      .tip-label {
        display: block;
        margin-bottom: 6px;
        font-size: var(--font-size-xs);
        font-weight: bold;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: var(--color-text-tertiary);
      }

      .greyed {
        font-size: clamp(13px, 3.5vw, 16px);
        color: var(--color-text-muted);
      }
    `,
  ];

  private uiState = bindValtio(this, uiStateProxy);

  @consume({ context: gameApiContext })
  private gameApi!: GameAPIType;

  private dismiss = () => {
    this.gameApi.dismissBigMessage();
  };

  /** Prompt to install the game as a home-screen app. Skipped when we are
   *  already running as one — iOS standalone web apps get their own storage
   *  bucket, so the `mobileshown` flag doesn't always carry over from Safari
   *  and the installed app can otherwise greet the user by telling them to
   *  install it. */
  private renderInstallTip() {
    if (this.uiState.isInstalled) return nothing;

    // iOS ignores the manifest's `orientation` member for home-screen apps, so
    // only the Chromium copy promises a landscape launch.
    return html`
      <div class="tip">
        <span class="tip-label">Tip</span>
        ${
          this.uiState.isIos
            ? 'Tap Share → “Add to Home Screen” for a fullscreen launch.'
            : 'Open your browser menu → “Install” (or “Add to Home screen”) for a fullscreen, landscape launch.'
        }
      </div>
    `;
  }

  /** A switch (rather than early returns) so `noImplicitReturns` turns a new
   *  `bigMessage` variant into a compile error instead of a blank overlay. */
  override render() {
    switch (this.uiState.bigMessage) {
      case 'mobile':
        return html`
          <div class="overlay scrollbar-thin" @click=${this.dismiss}>
            <div class="msg">Mobile mode</div>
            <div class="small">
              Airmash is built primarily for keyboard play, so the best experience is on a desktop or laptop. You’re
              currently using touch controls, which we support as best we can.
            </div>
            ${this.renderInstallTip()}
            <div class="aside">Bluetooth controllers work too — pair one and press any button.</div>
            <div class="greyed">Tap anywhere to close</div>
          </div>
        `;

      case 'webgl':
        return html`
          <div class="overlay scrollbar-thin">
            <div class="msg">WebGL required</div>
            <div class="small">
              This game requires a WebGL enabled browser in order to run.<br />
              Please allow WebGL for this domain to continue.
            </div>
          </div>
        `;

      case null:
        return nothing;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'big-message': BigMessage;
  }
}
